import React from "react";
import {Link} from "@montana/shared/link";
import {SvgIcon} from "@montana/shared/svg-icon";
import {useSiteStructure} from "@montana/containers/site-structure";
import {ISocialLink} from "@montana/models/generated/cms.interface";
import {Media} from "@montana/shared/media";
import {Responsive} from "@montana/shared/responsive";
import {TrackingTypes} from "@montana/helpers/tracking.helper";
import {
    LanguageAndMarketSelector
} from "@montana/shell/header/language-and-market-selector/language-and-market-selector";


interface IFooterListComponentProps {
    headline?: string;
    links?: ISocialLink[];
}


const FooterSocialLinkComponent: React.FC<IFooterListComponentProps> = React.memo(({
                                                                                       headline,
                                                                                       links
                                                                                   }: IFooterListComponentProps): JSX.Element => (
    <div className="footer__social-icons">
        <h3 className="footer__list-column-title">
            {headline}
        </h3>
        <div className="footer__social-links">
            {links && links.map(({
                                     url, target, title, iconType, customIcon
                                 }, index) => url && (
                <Link key={index} className="footer__social-link" to={url} target={target} title={title}
                      trackingType={TrackingTypes.Social} trackingAction={title}>
                    {customIcon ?
                        (
                            <Media className="footer__image-wrapper" aria-hidden={true} image={customIcon}
                                   mediaRatio={false} placeholder={false}/>
                        ) : (
                            <SvgIcon className="footer__svg-wrapper" iconName={iconType}/>
                        )
                    }
                </Link>
            ))}
        </div>
    </div>
));


const FooterListComponent: React.FC<IFooterListComponentProps> = React.memo(({
                                                                                 headline,
                                                                                 links
                                                                             }: IFooterListComponentProps): JSX.Element | null => {
        if (!links || !links.length) {
            return null;
        }

        return (
            <>
                <h3 className="footer__list-column-title">{headline}</h3>
                <ul className="footer__list-column-links">
                    {links && links.map(({
                                             url, target, name, title,
                                         }, index) => url && (
                        // Just using index as key, since this list does not change runtime
                        <li className="footer__list-column-link" key={index}>
                            <Link to={url} target={target} title={title} inverseStrokeAnimation withStroke>
                                {name}
                            </Link>
                        </li>
                    ))}
                </ul>
            </>
        )
    }
);

interface IFooterContactInformationProps {
    data?: {
        address?: string;
        zipCode?: string;
        city?: string;
        country?: string;
        phone?: string;
        email?: string;
        cvr?: string;
    };
}

const FooterContactInformation: React.FC<IFooterContactInformationProps> = React.memo(({data}: IFooterContactInformationProps): JSX.Element | null => {
    if (!data) {
        return null;
    }

    const {
        address, zipCode, city, country, phone, email, cvr,
    } = data;

    return (
        <>
            <div className="footer__contact-main">
                <p>
                    {address}
                    <br/>
                    {`${zipCode} ${city}`}
                    <br/>
                    {country}
                </p>
                <div className="footer__contact-links">
                    <Link
                        withStroke
                        to={`tel: ${phone?.trim()}`}
                        trackingType={TrackingTypes.Contact}
                        trackingAction={'phone'}
                    >
                        {phone}
                    </Link>
                    <Link
                        withStroke
                        to={`mailto: ${email?.trim()}`}
                        trackingType={TrackingTypes.Contact}
                        trackingAction={'email'}
                    >
                        {email}
                    </Link>
                </div>
            </div>
            <p>
                {`CVR: ${cvr}`}
            </p>
        </>
    );
});

export const Footer: React.FC = React.memo((): JSX.Element | null => {
    const {footer} = useSiteStructure();

    if (!footer) {
        return null;
    }

    const {
        companyName,
        address,
        zipCode,
        city,
        country,
        phone,
        email,
        cvr,
        copyright,
        columnOneTitle,
        columnOneLinks,
        columnTwoTitle,
        columnTwoLinks,
        columnThreeTitle,
        columnThreeLinks,
        socialColumnTitle,
        socialColumnLinks,
    } = footer;

    return (
        <footer className="footer">
            <div className="footer__container">
                <Responsive minWidth={1000}>
                    <h2 className="footer__contact-title">{companyName}</h2>
                    <div className="footer__contact">
                        <FooterContactInformation data={{
                            address, zipCode, city, country, phone, email, cvr,
                        }}
                        />
                    </div>
                </Responsive>
                <div className="footer__list-column">
                    <FooterListComponent headline={columnOneTitle} links={columnOneLinks}/>
                </div>
                <div className="footer__list-column">
                    <FooterListComponent headline={columnTwoTitle} links={columnTwoLinks}/>
                </div>

                <div className="footer__list-column">
                    <FooterListComponent headline={columnThreeTitle} links={columnThreeLinks}/>
                </div>
                <Responsive maxWidth={999} includeSSR>
                    <div className="footer__contact">
                        <h2 className="footer__contact-title">{companyName}</h2>
                        <FooterContactInformation data={{
                            address, zipCode, city, country, phone, email, cvr,
                        }}
                        />
                    </div>
                </Responsive>
                <div className="footer__social-container">
                    <div className="footer__market-selector">
                        <div className="footer__market-selector__dropdown">
                            <LanguageAndMarketSelector/>
                        </div>
                    </div>
                    <FooterSocialLinkComponent headline={socialColumnTitle} links={socialColumnLinks}/>
                </div>
                <p className="footer__copyright">{copyright}</p>
            </div>
        </footer>
    );
});
